import React from 'react'
import PropTypes from 'prop-types'
import debounce from 'lodash/debounce'
import { handleTypeahead } from './utils'
import {
  ANALYTICS_CATEGORY,
  CABIN_CLASSES,
  CALENDAR_POPOVER_SELECTOR,
  ECONOMY,
  FLY,
  ROUND_TRIP
} from '../../constants/Constants'
import { ExperimentContext } from '../../contexts'
import { send } from '../../utils/PCLN-Analytics'

const TRAVELER_TYPE_DICTIONARY = {
  ADULTS: 'adultsCount',
  YOUTHS: 'youthsCount',
  CHILDREN: 'childrenCount',
  LAP_INFANTS: 'infantsCount',
  ROOMS: 'roomCount'
}

const PRODUCT_OPTIONS = [
  {
    name: 'Hotel',
    type: 'STAY',
    label: 'Add a hotel',
    gaLabel: 'add hotel',
    id: 'add-a-hotel-checkbox'
  },
  {
    name: 'Car',
    type: 'DRIVE',
    label: 'Add a car',
    gaLabel: 'add car',
    id: 'add-a-car-checkbox'
  }
]

const setFlight = (data, index) => state => {
  return {
    flights: [
      ...state.flights.slice(0, index),
      {
        ...state.flights[index],
        ...data
      },
      ...state.flights.slice(index + 1)
    ]
  }
}

/**
 * updates the dropdownItems at a given index with the incoming typeahead data.
 * performs a merge if the incoming results key differs from what's in dropdownItems.
 *
 * examples:
 * dropdownitems = [{ departureAirport: ['A', 'B', 'C'] }]
 * data = { arrivalAirport: ['D', 'E', 'F'] }
 *
 * returns dropdownItems: [{ departureAirport: ['A', 'B', 'C'], arrivalAirport: ['D', 'E', 'F'] }]
 *
 * dropdownItems is essentially a lookup table by index,
 * which is why it combines the departureAirport and arrivalAirport and associates
 * them with an index that is shared among two different type ahead search forms
 *
 */
const setDropdownItems = (data, index) => state => {
  return {
    dropdownItems: [
      ...state.dropdownItems.slice(0, index),
      {
        ...state.dropdownItems[index],
        ...data
      },
      ...state.dropdownItems.slice(index + 1)
    ]
  }
}

const setRecent = (index, data) => state => {
  let recentSearches = [...state.recentSearches]
  const [key] = Object.keys(data)

  if (recentSearches[index]) {
    if (recentSearches[index][key]) {
      const hasResult = recentSearches[index][key].some(item => {
        return item.content === data[key][0].content
      })
      if (!hasResult) {
        recentSearches[index][key] = recentSearches[index][key].length === 3
          ? recentSearches[index][key].slice(1, 3).concat(data[key])
          : recentSearches[index][key].concat(data[key])
      }
    } else {
      recentSearches = [{ ...recentSearches[index], ...data }]
    }
  } else {
    recentSearches[index] = data
  }

  return { recentSearches }
}

const addFlight = state => {
  const previousFlight = state.flights[state.flights.length - 1] || {}
  const nextFlight = previousFlight.arrivalAirport
    ? {
      departureAirport: previousFlight.arrivalAirport
    }
    : {}
  return {
    flights: [...state.flights, nextFlight].slice(0, 5)
  }
}

const removeFlight = i => state => ({
  flights: [...state.flights.slice(0, i), ...state.flights.slice(i + 1)]
})

const open = key => state => ({ [key]: true })
const close = key => state => ({ [key]: false })
const openTravelerDropdown = open('travelerDropdownOpen')
const closeTravelerDropdown = close('travelerDropdownOpen')
const openCalendar = i => state => ({ calendarOpen: i })
const closeCalendar = close('calendarOpen')

const withState = Component => {
  class WithFlightState extends React.Component {
    static contextType = ExperimentContext
    static defaultProps = {
      adultsCount: 1,
      cabinClass: ECONOMY,
      cabinClasses: CABIN_CLASSES,
      childrenCount: 0,
      closeCalendar,
      closeTravelerDropdown,
      dropdownItems: [],
      debounce: 20,
      flights: [{}],
      infantsCount: 0,
      isMobile: false,
      openCalendar,
      openTravelerDropdown,
      productType: FLY,
      productsOptions: PRODUCT_OPTIONS,
      recentSearches: [],
      selectedProducts: [FLY],
      tripType: ROUND_TRIP,
      youthsCount: 0,
      roomCount: 1
    }

    static propTypes = {
      isMobile: PropTypes.bool,
      type: PropTypes.string,
      cacheKey: PropTypes.string,
      debounce: PropTypes.number,
      calendarOpen: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
      travelerDropdownOpen: PropTypes.bool,
      handleTripTypeChange: PropTypes.func,
      handleProductTypeChange: PropTypes.func,
      handleTravelerChange: PropTypes.func
    }

    saveForm = debounce(data => {
      const { cacheKey } = this.props
      if (cacheKey) {
        window.localStorage.setItem(cacheKey, JSON.stringify(
          Object.assign({}, data, { dropdownItems: data.recentSearches }))
        )
      }
    }, this.props.debounce)

    constructor (props) {
      super(props)

      this.state = {
        ...props,
        calendarOpen:
          typeof props.calendarOpen === 'number' ? props.calendarOpen : false,
        travelerDropdownOpen: props.travelerDropdownOpen || false
      }
    }

    handleChange = (data) => {
      this.setState(data)

      if (this.props.handleProductTypeChange) {
        if (data.tripType) {
          this.props.handleTripTypeChange(data.tripType)
        } else if (data.productType) {
          this.props.handleProductTypeChange(data)
        }
      }
    }

    /**
     * set dropdownItems in state which typeahead Dropdown displays
     *
     * @param {number} index - index of a flight search form, there can be more than 1 for a multi-destination trip
     * @param {object} data - results from the typeahead service lookup
     */
    handleTypeaheadChange = (index, data) => {
      const key = Object.keys(data)
      const currentQuery = data[key]
      const { isMobile = false } = this.props
      const isParsingFlightLocation = false
      handleTypeahead(currentQuery, isMobile, isParsingFlightLocation)
        .then(data => {
          if (data && Array.isArray(data) && data.length) {
            data.forEach((item, idx) => {
              data[idx].content = item.itemName
            })
            this.setState(setDropdownItems({ [key]: data }, index))
          } else {
            // this is how we preserve recent searches after
            // someone interacts with the typeahead fields
            this.setState({
              dropdownItems: this.state.recentSearches
            })
          }
        })
        .catch(err => {
          // TODO: add logging
          console.warn('Error fetching typeahead: ', err)
        })
    }

    handleFlightChange = (i, data) => {
      this.setState(setFlight(data, i))
    }

    handleTravelerChange = data => {
      const stateKey = TRAVELER_TYPE_DICTIONARY[data.travelerType]
      const newTravelerState = {}
      const isNewCountNumber = typeof data.count === 'number'

      if (this.props.handleTravelerChange) {
        this.props.handleTravelerChange(data)
      }
      newTravelerState[stateKey] = isNewCountNumber
        ? data.count
        : this.state[stateKey]
      this.setState(newTravelerState)
    }

    /**
     * on selecting a typeahead option from the dropdown do
     *
     * 1) save the flight to state
     * 2) set isRecent = true and save it to local storage for later use
     * todo: set 30 day limit
     */
    handleTypeaheadSelection = (index, data) => {
      const key = Object.keys(data)
      const flight = data[key]

      // this removes the flight from state if there's no itemName
      // is no itemName a thing that happens? how can we verify this?
      if (flight && !flight.itemName) {
        const flightFromState = Object.assign({}, this.state.flights[index])
        delete flightFromState[key]
        return this.setState(setFlight(flightFromState, index))
      }

      this.setState(setFlight(data, index))
      if (data[key].isRecent) {
        send({
          action: 'Flight Recent Search',
          category: ANALYTICS_CATEGORY,
          data: key[0] === 'departureAirport' ? 'Departing' : 'Going to'
        })
      } else if (!this.props.isMobile) {
        data[key].isRecent = true
      }
      this.setState(setRecent(index, { [key]: [data[key]] }), () => {
        const { dropdownItems, recentSearches } = this.state
        this.saveForm({
          dropdownItems,
          recentSearches
        })
      })
    }

    handleAddFlight = () => {
      this.setState(addFlight)
    }

    handleRemoveFlight = i => {
      this.setState(removeFlight(i))
    }

    update = (...args) => {
      this.setState(...args)
    }

    toggleRecent = (e) => {
      if (
        e.target &&
        (
          e.target.id.indexOf('flight-departure-airport') > -1 ||
          e.target.id.indexOf('flight-arrival-airport') > -1
        )
      ) {
        if (e.target.value !== '') {
          this.setState({
            dropdownItems: []
          })
        } else {
          this.setState({
            dropdownItems: this.state.recentSearches
          })
        }
      }
    }

    handleDocumentClick = e => {
      if (e.target.closest('[data-calendar]') || e.target.closest(CALENDAR_POPOVER_SELECTOR)) {
        return
      }
      this.update(closeCalendar)
      this.update(closeTravelerDropdown)
    }

    componentDidUpdate (prevProps, prevState) {
      if (prevState.tripType !== this.state.tripType) {
        if (Array.isArray(this.state.flights) && this.state.flights.length > 1) {
          // eslint-disable-next-line react/no-did-update-set-state
          this.setState({ flights: [this.state.flights[0]] })
        }
      }
    }

    componentDidMount () {
      const { cacheKey } = this.props

      if (!cacheKey || !window.localStorage.getItem(cacheKey)) {
        return
      }

      try {
        const data = JSON.parse(window.localStorage.getItem(cacheKey))
        this.setState(data)
      } catch (e) {
        console.error('error fetching local results') // is this helpful outside of debugging?
      }
    }

    render () {
      return (
        <Component
          {...this.state}
          onChange={this.handleChange}
          onFlightChange={this.handleFlightChange}
          onTravelerChange={this.handleTravelerChange}
          onTypeaheadUpdate={this.handleTypeaheadChange}
          onTypeaheadSelection={this.handleTypeaheadSelection}
          onAddFlight={this.handleAddFlight}
          onRemoveFlight={this.handleRemoveFlight}
          update={this.update}
          handleDocumentClick={this.handleDocumentClick}
          handleSubmit={this.handleSubmit}
          toggleRecent={this.toggleRecent}
        />
      )
    }
  }

  WithFlightState.displayName = `WithFlightState${Component.displayName || Component.name || 'Component'}`
  return WithFlightState
}

export default withState
